import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  useTheme
} from "@mui/material";
import axios from "../../api/axiosInstance";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import GppGoodIcon from "@mui/icons-material/GppGood";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";

const SubjectUserAccess = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Data states
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog and form states
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [priceInput, setPriceInput] = useState("");

  const [selectedEnrollUser, setSelectedEnrollUser] = useState(null);
  const [selectedEnrollCourse, setSelectedEnrollCourse] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch all necessary data from admin API
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [coursesRes, usersRes, enrollmentsRes] = await Promise.all([
        axios.get("/admin/courses"),
        axios.get("/admin/users"),
        axios.get("/admin/enrollments")
      ]);
      setCourses(coursesRes.data);
      setUsers(usersRes.data);
      setEnrollments(enrollmentsRes.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Failed to fetch administrative data. Ensure you are signed in as admin.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Update course price
  const handleOpenPriceDialog = (course) => {
    setSelectedCourse(course);
    setPriceInput(course.price.toString());
    setPriceDialogOpen(true);
  };

  const handleClosePriceDialog = () => {
    setPriceDialogOpen(false);
    setSelectedCourse(null);
    setPriceInput("");
  };

  const handleSavePrice = async () => {
    if (!selectedCourse) return;
    const price = parseInt(priceInput, 10);
    if (isNaN(price) || price < 0) {
      alert("Please enter a valid non-negative number for the price.");
      return;
    }

    setActionLoading(true);
    try {
      const response = await axios.post(`/admin/course/${selectedCourse.id}/price`, { price });
      // Update local state
      setCourses(courses.map(c => c.id === selectedCourse.id ? response.data : c));
      handleClosePriceDialog();
      // Refetch enrollments/data to keep in sync if needed
      const enrollmentsRes = await axios.get("/admin/enrollments");
      setEnrollments(enrollmentsRes.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update course price");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemovePrice = async (course) => {
    if (!window.confirm(`Are you sure you want to make the subject "${course.title}" free?`)) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await axios.post(`/admin/course/${course.id}/price`, { price: 0 });
      setCourses(courses.map(c => c.id === course.id ? response.data : c));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to make course free");
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleVideosUnlocked = async (course) => {
    setActionLoading(true);
    try {
      const response = await axios.post(`/admin/course/${course.id}/toggle-videos-unlocked`);
      setCourses(courses.map(c => c.id === course.id ? response.data : c));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to toggle video access");
    } finally {
      setActionLoading(false);
    }
  };

  // Grant access (Enroll user)
  const handleGrantAccess = async () => {
    if (!selectedEnrollUser) {
      alert("Please select a user.");
      return;
    }
    if (!selectedEnrollCourse) {
      alert("Please select a subject.");
      return;
    }

    setActionLoading(true);
    try {
      const response = await axios.post("/admin/enroll", {
        user_id: selectedEnrollUser.id,
        course_id: selectedEnrollCourse.id
      });
      
      // Update enrollments state by checking if it already exists in the local state
      if (!enrollments.some(e => e.id === response.data.id)) {
        // Refetch enrollments to get fully populated nested user and course models
        const enrollmentsRes = await axios.get("/admin/enrollments");
        setEnrollments(enrollmentsRes.data);
      }
      setSelectedEnrollUser(null);
      setSelectedEnrollCourse(null);
      alert("Access granted successfully!");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to grant access");
    } finally {
      setActionLoading(false);
    }
  };

  // Revoke access (Unenroll user)
  const handleRevokeAccess = async (userId, courseId, userName, courseTitle) => {
    if (!window.confirm(`Are you sure you want to revoke access to "${courseTitle}" for user "${userName}"?`)) {
      return;
    }

    setActionLoading(true);
    try {
      await axios.post("/admin/unenroll", {
        user_id: userId,
        course_id: courseId
      });
      // Filter out deleted enrollment
      setEnrollments(enrollments.filter(e => !(e.user_id === userId && e.course_id === courseId)));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to revoke access");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8, gap: 2 }}>
        <CircularProgress color="primary" />
        <Typography variant="body1">Loading system data...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header and Refresh */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GppGoodIcon color="primary" /> Subject & User Access Controls
        </Typography>
        <Tooltip title="Refresh Data">
          <IconButton onClick={fetchData} disabled={actionLoading} sx={{ border: "1px solid rgba(0,0,0,0.1)" }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      {/* Main Grid for Subject Pricing & Grant Access Form */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        
        {/* Subject Pricing Table */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '16px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              height: '100%'
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Subject Pricing
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure subject prices. Setting price &gt; ₹0 automatically locks its premium content for non-enrolled users.
            </Typography>

            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Video Access</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {courses.map((course) => (
                    <TableRow key={course.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{course.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{course.slug}</Typography>
                      </TableCell>
                      <TableCell>
                        {course.is_paid ? (
                          <Chip label="Paid" size="small" color="primary" variant="outlined" />
                        ) : (
                          <Chip label="Free" size="small" color="success" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {course.price > 0 ? `₹${course.price}` : "Free"}
                      </TableCell>
                      <TableCell>
                        {course.videos_unlocked_for_all ? (
                          <Chip label="Unlocked for All" size="small" color="success" />
                        ) : (
                          <Chip label="Login / Enrollment Required" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title={course.videos_unlocked_for_all ? "Restrict videos to logged-in/enrolled users" : "Unlock videos for all users (no login required)"}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleVideosUnlocked(course)}
                              color={course.videos_unlocked_for_all ? "success" : "default"}
                              disabled={actionLoading}
                            >
                              {course.videos_unlocked_for_all ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Set/Edit Price">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenPriceDialog(course)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {course.is_paid && (
                            <Tooltip title="Remove Price (Make Free)">
                              <IconButton
                                size="small"
                                onClick={() => handleRemovePrice(course)}
                                color="error"
                              >
                                <HighlightOffIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                  ))}
                  {courses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        No subjects/courses found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Grant Access Form */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '16px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              Grant User Access
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Directly enroll a user into a paid subject. This gives them instant access bypasses the payment gateway.
            </Typography>

            <Stack spacing={3} sx={{ flexGrow: 1 }}>
              {/* Select User Autocomplete */}
              <Autocomplete
                options={users}
                getOptionLabel={(option) => `${option.username || 'Google User'} (${option.email})`}
                value={selectedEnrollUser}
                onChange={(event, newValue) => setSelectedEnrollUser(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search / Select User"
                    placeholder="Type name or email..."
                    variant="outlined"
                    fullWidth
                  />
                )}
              />

              {/* Select Subject */}
              <FormControl fullWidth>
                <InputLabel id="enroll-course-select-label">Select Subject</InputLabel>
                <Select
                  labelId="enroll-course-select-label"
                  value={selectedEnrollCourse || ""}
                  label="Select Subject"
                  onChange={(e) => setSelectedEnrollCourse(e.target.value)}
                >
                  {courses.map((course) => (
                    <MenuItem key={course.id} value={course}>
                      {course.title} ({course.is_paid ? `Paid - ₹${course.price}` : "Free"})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ pt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<PersonAddIcon />}
                  onClick={handleGrantAccess}
                  disabled={actionLoading || !selectedEnrollUser || !selectedEnrollCourse}
                  sx={{
                    py: 1.5,
                    borderRadius: "12px",
                    fontWeight: 700,
                    textTransform: "none",
                    background: "linear-gradient(135deg, #e50914 0%, #6a0dad 100%)",
                    color: "white"
                  }}
                >
                  Grant Course Access
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Enrollments List / Revoke Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '16px',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          Active User Enrollments
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Review which users have active access to which subjects, and revoke access as necessary.
        </Typography>

        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Granted At</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enrollments.map((enrollment) => {
                const userName = enrollment.user?.username || "Google User";
                const userEmail = enrollment.user?.email || "Unknown Email";
                const courseTitle = enrollment.course?.title || "Unknown Subject";
                
                return (
                  <TableRow key={enrollment.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{userName}</Typography>
                      <Typography variant="caption" color="text.secondary">{userEmail}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{courseTitle}</Typography>
                      <Typography variant="caption" color="text.secondary">{enrollment.course?.slug}</Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(enrollment.enrolled_at).toLocaleDateString()}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {new Date(enrollment.enrolled_at).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleRevokeAccess(enrollment.user_id, enrollment.course_id, userName, courseTitle)}
                        disabled={actionLoading}
                        sx={{ borderRadius: "8px", textTransform: "none" }}
                      >
                        Revoke Access
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {enrollments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    No active student enrollments found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Set Price Dialog */}
      <Dialog open={priceDialogOpen} onClose={handleClosePriceDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Set Subject Price
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, pb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Subject: <strong>{selectedCourse?.title}</strong>
            </Typography>
            <TextField
              label="Price (in Rupees)"
              type="number"
              fullWidth
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
              variant="outlined"
              inputProps={{ min: 0 }}
              helperText="Set to 0 to make the subject free."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClosePriceDialog} disabled={actionLoading} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePrice}
            variant="contained"
            color="primary"
            disabled={actionLoading}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              background: "linear-gradient(135deg, #e50914 0%, #6a0dad 100%)",
              color: "white"
            }}
          >
            Save Price
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubjectUserAccess;
